import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Alert,
  Button,
  Row,
  Col,
} from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true, 
      error, 
      errorInfo: null 
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console and external service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Send to analytics/monitoring service
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: error.toString(),
        fatal: false,
      });
    }
  }

  handleRestart = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Row className="error-boundary" style={{ padding: '2rem' }}>
          <Col span={24}>
            <Alert
              type="error"
              message="Something went wrong"
              description="An unexpected error occurred in this component."
              showIcon
            />
            
            <div style={{ 
              marginTop: '1rem', 
              padding: '1rem',
              backgroundColor: 'var(--color-bg-primary)',
              borderRadius: '4px',
              border: '1px solid var(--color-border-primary)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '1rem'
              }}>
                <WarningOutlined style={{ fontSize: '20px' }} />
                <h3 style={{ margin: 0, fontSize: '1rem' }}>Error Details</h3>
              </div>
              
              <details style={{ fontSize: '0.875rem' }}>
                <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>
                  Error Message
                </summary>
                <pre style={{ 
                  whiteSpace: 'pre-wrap', 
                  fontSize: '0.75rem',
                  backgroundColor: 'var(--color-bg-secondary)',
                  padding: '0.5rem',
                  borderRadius: '2px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {this.state.error && this.state.error.toString()}
                </pre>
              </details>

              {this.state.errorInfo && (
                <details style={{ fontSize: '0.875rem', marginTop: '1rem' }}>
                  <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>
                    Component Stack
                  </summary>
                  <pre style={{ 
                    whiteSpace: 'pre-wrap', 
                    fontSize: '0.75rem',
                    backgroundColor: 'var(--color-bg-secondary)',
                    padding: '0.5rem',
                    borderRadius: '2px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <Button
                type="primary"
                size="middle"
                icon={<ReloadOutlined />}
                onClick={this.handleRestart}
              >
                Try Again
              </Button>
              
              <Button
                type="default"
                size="middle"
                style={{ marginLeft: '1rem' }}
                onClick={() => window.location.reload()}
              >
                Reload Page
              </Button>
            </div>
          </Col>
        </Row>
      );
    }

    return this.props.children;
  }
}

// Hook-based error boundary for functional components
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
  onError?: (error: Error, errorInfo: ErrorInfo) => void
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback} onError={onError}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Async error boundary for handling async errors
export const useAsyncError = () => {
  const [, setError] = React.useState();
  
  return React.useCallback(
    (error: Error) => {
      setError(() => {
        throw error;
      });
    },
    [setError]
  );
};